import {
  UtensilsCrossed,
  Clock,
  Users,
  DollarSign,
  AlertTriangle,
  Leaf,
  ChefHat,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import type { Recipe } from '../../types';
import {
  RECIPE_TYPE_LABELS,
  DIFFICULTY_LABELS,
  DIFFICULTY_COLORS,
} from '../../types';

interface RecipeCardProps {
  readonly recipe: Recipe;
  readonly onClick?: () => void;
}

const typeColors = {
  finished: 'bg-blue-100 text-blue-800',
  'sub-recipe': 'bg-purple-100 text-purple-800',
  component: 'bg-gray-100 text-gray-800',
};

const ALLERGEN_LABELS: Record<string, string> = {
  milk: 'Milk',
  eggs: 'Eggs',
  fish: 'Fish',
  shellfish: 'Shellfish',
  'tree-nuts': 'Tree Nuts',
  peanuts: 'Peanuts',
  wheat: 'Wheat',
  soybeans: 'Soybeans',
  sesame: 'Sesame',
};

const DIETARY_FLAG_LABELS: Record<string, string> = {
  vegan: 'Vegan',
  vegetarian: 'Vegetarian',
  'gluten-free': 'GF',
  'dairy-free': 'DF',
  halal: 'Halal',
  kosher: 'Kosher',
};

/**
 * Card component displaying a recipe.
 */
export function RecipeCard({
  recipe, onClick 
}: RecipeCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-lg border bg-white p-4 text-left transition-shadow hover:shadow-md"
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-primary/10 p-2">
            <UtensilsCrossed className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-medium text-gray-900">{recipe.name}</h3>
            {recipe.cuisine && (
              <p className="text-sm text-gray-500">{recipe.cuisine}</p>
            )}
          </div>
        </div>

        <span className={cn('rounded-full px-2 py-1 text-xs font-medium', typeColors[recipe.type])}>
          {RECIPE_TYPE_LABELS[recipe.type]}
        </span>
      </div>

      {/* Description */}
      {recipe.description && (
        <p className="mt-2 text-sm text-gray-600 line-clamp-2">
          {recipe.description}
        </p>
      )}

      {/* Meta info */}
      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-gray-500">
        {recipe.totalTimeMinutes && (
          <span className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            {recipe.totalTimeMinutes} min
          </span>
        )}
        <span className="flex items-center gap-1">
          <Users className="h-4 w-4" />
          {recipe.servings} servings
        </span>
        {recipe.costPerServing && (
          <span className="flex items-center gap-1">
            <DollarSign className="h-4 w-4" />
            ${recipe.costPerServing.toFixed(2)}
          </span>
        )}
        {recipe.difficulty && (
          <span className={cn('flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', DIFFICULTY_COLORS[recipe.difficulty])}>
            <ChefHat className="h-3 w-3" />
            {DIFFICULTY_LABELS[recipe.difficulty]}
          </span>
        )}
      </div>

      {/* Category and ingredients count */}
      <div className="mt-3 flex flex-wrap gap-2">
        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
          {recipe.category}
        </span>
        <span className="inline-flex items-center rounded-full bg-gray-50 px-2 py-1 text-xs text-gray-600">
          {recipe.ingredients.length} ingredients
        </span>
        {recipe.subRecipeIds && recipe.subRecipeIds.length > 0 && (
          <span className="inline-flex items-center rounded-full bg-purple-50 px-2 py-1 text-xs text-purple-600">
            {recipe.subRecipeIds.length} sub-recipe{recipe.subRecipeIds.length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Allergens */}
      {recipe.allergens.length > 0 && (
        <div className="mt-3 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <div className="flex flex-wrap gap-1">
            {recipe.allergens.map((allergen) => (
              <span
                key={allergen}
                className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700"
              >
                {ALLERGEN_LABELS[allergen] ?? allergen}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Dietary Flags */}
      {recipe.dietaryFlags.length > 0 && (
        <div className="mt-2 flex items-center gap-2">
          <Leaf className="h-4 w-4 text-green-500" />
          <div className="flex flex-wrap gap-1">
            {recipe.dietaryFlags.map((flag) => (
              <span
                key={flag}
                className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700"
              >
                {DIETARY_FLAG_LABELS[flag] ?? flag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Version */}
      <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
        <span>v{recipe.version}</span>
        <span>Updated {new Date(recipe.updatedAt).toLocaleDateString()}</span>
      </div>
    </button>
  );
}
